import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { createEventEmail, getEmailTemplate } from 'src/shared/services/eventService';
import { toast } from 'react-toastify';
import { EventParticipant } from '../models/eventParticipant';
import { useSchemaSendEmail } from '../schemas/useSchemaSendEmail';
import { EventSendEmailRequest } from '../models/eventSendEamilRequest';
import { EventParticipantsSendEmailRecipients } from './eventParticipantsSendEmailRecipients';

type EventParticipantSendEmailModalProps = {
  modalOpen: boolean;
  eventId: number;
  selectedEventParticipants: EventParticipant[];
  onSave: () => void;
  onClose: () => void;
};

export const EventParticipantSendEmailModal = (props: EventParticipantSendEmailModalProps) => {
  const { modalOpen, onSave, onClose, eventId, selectedEventParticipants } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const modalTop = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const sendEmailRequestSchema = useSchemaSendEmail();
  type SendEmailFormInterface = InferType<typeof sendEmailRequestSchema>;

  const formMethods = useForm<SendEmailFormInterface>({
    resolver: yupResolver(sendEmailRequestSchema),
    defaultValues: {
      subject: '',
      body: '',
    },
    mode: 'all',
  });
  const { handleSubmit, setValue } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getEmailTemplate(eventId);
        if (isMounted()) setValue('body', res.data);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchData();
  }, [eventId, isMounted, setValue]);

  const onSubmit = useCallback(
    async (data: SendEmailFormInterface) => {
      const dataToPost: EventSendEmailRequest = {
        subject: data.subject,
        body: data.body,
        participantIds: selectedEventParticipants.map((x) => x.id),
      };

      try {
        await createEventEmail(eventId, dataToPost);
        if (isMounted()) toast.success(<>{t('event:eventEmailSuccess')}</>);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      } finally {
        if (isMounted()) onSave();
      }
    },
    [eventId, isMounted, onSave, selectedEventParticipants, t],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('event:sendParticipantMessage')}
      disableEnforceFocus
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box ref={modalTop} sx={{ position: 'relative' }}>
          <LoadingOverlay loading={loading} />
          <Box sx={{ p: 3 }}>
            <GridWrapper>
              <Grid item xs={12}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <GridWrapper>
                      <Grid item xs={12}>
                        <EventParticipantsSendEmailRecipients selectedEventParticipants={selectedEventParticipants} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField name='subject' label={t('event:formFields.heading')} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor name='body' noMargin />
                      </Grid>
                    </GridWrapper>
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
    />
  );
};
